// frontend/src/pages/LibraryPage.js
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import CategoriesGrid from '../components/library/CategoriesGrid';
import BookCarousel from '../components/library/BookCarousel';
import BookCover from '../components/library/BookCover';
import ChallengeCard from '../components/library/ChallengeCard';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { fetchBookCategories as fetchCategories, fetchBooks } from '../services/bookService';

// بنر اول (بین کراسل دوم و سوم)
const BannerLibrary = () => {
  const bannerUrl = 'https://s3.afra.kubit.cloud/mahsacanva1365-far-free/profile/7.png';
  return (
    <Link to="/products/list/tag/Book-review-and-criticism" className="block w-full my-6 transition-transform hover:scale-[1.01] duration-300">
      <img
        src={bannerUrl}
        alt="نقد و بررسی کتاب"
        className="w-full rounded-xl shadow-md object-cover"
        loading="lazy"
      />
    </Link>
  );
};

// بنر دوم (بین کراسل چهارم و پنجم)
const BannerLibrarySecond = () => {
  const bannerUrl = 'https://s3.afra.kubit.cloud/mahsacanva1365-far-free/profile/8.png';
  return (
    <Link to="/books?tags__slug=The-Ten-Influential-Books" className="block w-full my-6 transition-transform hover:scale-[1.01] duration-300">
      <img
        src={bannerUrl}
        alt="ده کتاب اثرگذار"
        className="w-full rounded-xl shadow-md object-cover"
        loading="lazy"
      />
    </Link>
  );
};

const LibraryPage = () => {
    const [sortedCategories, setSortedCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [latestBooks, setLatestBooks] = useState([]);
    const [loadingLatest, setLoadingLatest] = useState(true);

    useEffect(() => {
        const loadData = async () => {
            try {
                const catsResponse = await fetchCategories();
                let fetchedCategories = Array.isArray(catsResponse.data)
                    ? catsResponse.data
                    : catsResponse.data?.results || [];

                if (fetchedCategories.length === 0) {
                    setSortedCategories([]);
                    setLoading(false);
                    return;
                }

                // برای هر دسته، جدیدترین کتاب و وجود محتوا را بررسی می‌کنیم
                const categoriesWithInfo = await Promise.all(
                    fetchedCategories.map(async (category) => {
                        try {
                            const res = await fetchBooks({
                                category__slug: category.slug,
                                ordering: '-created_at',
                                page_size: 1,
                            });
                            const books = res.data?.results || [];
                            let latestDate = 0;
                            let hasContent = books.length > 0;
                            if (hasContent && books[0].created_at) {
                                const dateValue = Date.parse(books[0].created_at);
                                if (!isNaN(dateValue)) latestDate = dateValue;
                                else latestDate = 1;
                            } else if (hasContent && books[0].updated_at) {
                                const dateValue = Date.parse(books[0].updated_at);
                                if (!isNaN(dateValue)) latestDate = dateValue;
                                else latestDate = 1;
                            } else if (hasContent) {
                                latestDate = 1;
                            }
                            return { ...category, latestDate, hasContent };
                        } catch (err) {
                            console.error(`Error fetching books for category ${category.slug}:`, err);
                            return { ...category, latestDate: 0, hasContent: false };
                        }
                    })
                );

                const categoriesWithContent = categoriesWithInfo.filter(cat => cat.hasContent);
                const sorted = [...categoriesWithContent].sort((a, b) => b.latestDate - a.latestDate);
                setSortedCategories(sorted);
            } catch (error) {
                console.error("Error loading library data:", error);
                setSortedCategories([]);
            } finally {
                setLoading(false);
            }
        };
        loadData();

        // واکشی جدیدترین کتاب‌ها (برای بخش شبکه ۳×۳)
        const loadLatestBooks = async () => {
            try {
                const response = await fetchBooks({ ordering: '-created_at', page_size: 9 });
                const books = response.data?.results || [];
                setLatestBooks(books);
            } catch (err) {
                console.error("Error fetching latest books:", err);
                setLatestBooks([]);
            } finally {
                setLoadingLatest(false);
            }
        };
        loadLatestBooks();
    }, []);

    if (loading) return <LoadingSpinner />;

    const defaultOgImage = 'https://s3.afra.kubit.cloud/mahsacanva1365-far-free/profile/og-image.jpg';
    const topCategories = sortedCategories.slice(0, 9);

    const firstThreeCarousels = sortedCategories.slice(0, 3);
    const remainingCarousels = sortedCategories.slice(3);

    return (
        <>
            <Helmet>
                <title>کتابخانه | فرزانه</title>
                <meta name="description" content="کتابخانه فرزانه: مجموعه‌ای از کتاب‌های فلسفی، روانشناسی، هنر زندگی و آثار مصطفی ملکیان." />
                <meta property="og:title" content="کتابخانه فرزانه" />
                <meta property="og:description" content="کتاب‌های تخصصی و عمومی در حوزه فلسفه و علوم انسانی." />
                <meta property="og:image" content={defaultOgImage} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={window.location.href} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="کتابخانه فرزانه" />
                <meta name="twitter:description" content="کتاب‌های تخصصی و عمومی در حوزه فلسفه و علوم انسانی." />
                <meta name="twitter:image" content={defaultOgImage} />
            </Helmet>

            <div className="library-page w-full max-w-xl mx-auto py-3 px-3 rtl pb-20">
                {/* گرید دسته‌بندی */}
                {topCategories.length > 0 && (
                    <section className="mb-8">
                        <CategoriesGrid categories={topCategories} />
                    </section>
                )}

                {/* بخش جدیدترین کتاب‌ها (شبکه ۳×۳ به جای کراسل) */}
                <section className="mb-8">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-[14px] text-gray-800 font-medium">جدیـدتـرین کــتـاب‌ها</h2>
                        <Link 
                            to="/books?ordering=-created_at" 
                            className="text-xs text-gray-400 hover:text-purple-800 flex items-center gap-1"
                        >
                            <span>بیشتر</span>
                            <ChevronLeft size={16} />
                        </Link>
                    </div>
                    {loadingLatest ? (
                        <div className="flex justify-center py-4">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-700"></div>
                        </div>
                    ) : latestBooks.length === 0 ? (
                        <div className="text-center text-gray-500 py-4">کتابی یافت نشد.</div>
                    ) : (
                        <div className="grid grid-cols-3 gap-5 pl-4 pr-4">
                            {latestBooks.map(book => (
                                <BookCover key={book.slug} book={book} />
                            ))}
                        </div>
                    )}
                </section>

                {/* چالش کتاب */}
                <section className="mb-8">
                    <ChallengeCard />
                </section>

                {/* بنر اول (بین کراسل دوم و سوم) */}
                <BannerLibrary />

                {/* سه کراسل اول پیشنهادی موضوعی */}
                {firstThreeCarousels.map((category, idx) => (
                    <BookCarousel
                        key={category.slug || idx}
                        title={category.title}
                        fetchParams={{ category__slug: category.slug, ordering: '-created_at', limit: 10 }}
                        viewMoreLink={`/books?category__slug=${category.slug}`}
                    />
                ))}

                {/* بنر دوم (بعد از سومین کراسل پیشنهادی) */}
                {firstThreeCarousels.length > 0 && <BannerLibrarySecond />}

                {/* بقیه کراسل‌های پیشنهادی موضوعی */}
                {remainingCarousels.map((category, idx) => (
                    <BookCarousel
                        key={category.slug || idx}
                        title={category.title}
                        fetchParams={{ category__slug: category.slug, ordering: '-created_at', limit: 10 }}
                        viewMoreLink={`/books?category__slug=${category.slug}`}
                    />
                ))}
            </div>
        </>
    );
};

export default LibraryPage;